import {IGatsbyImageData} from 'gatsby-plugin-image';
import {SectionMaxWidth, SectionMargin, SectionRounded, SectionSpacing} from '../components/layout/Section';
import {Colors} from './colorTypes';
import {LocaleEnum, LocaleAliasEnum, SlugLocale} from './locale';
import type {NodeWithContentProp} from '../utils/templateUtils';
import {BlogCategories} from './blogCategories';
import {PageContext} from './page';
import {PriceUnit} from '../components/dato/block/pricing/Price';
import {IconName} from '../components/icon/SvgIcon';
import {UUID} from 'react-accessible-accordion/dist/types/components/ItemContext';
import {Visibility} from './visibilityTypes';
import {CurrencySymbolEnum} from '../utils/Currency';

// Utility type
export type Mutable<Type> = {
  -readonly [Key in keyof Type]: Type[Key];
};

export type ModelBaseType = {
  originalId: string;
  model: {
    originalId: string;
  };
};

export enum Template {
  BLOG_POST = 'blogPost',
  GLOSSARY_OVERVIEW = 'glossaryOverview',
  PAGE = 'page'
}

export enum TOCFontSizeEnum {
  SMALL = 'small',
  DEFAULT = 'default'
}

export enum ImageSlimWidth {
  XSMALL = 'xsmall',
  SMALL = 'small',
  NORMAL = 'normal',
  LARGE = 'large',
  XLARGE = 'xlarge',
  FULL = 'full'
}

type ToExclude =
  | 'id'
  | 'parent'
  | 'children'
  | 'internal'
  | 'locale'
  | 'bodyNode'
  | 'meta'
  | 'model'
  | 'originalId'
  | '_allLabelLocales'
  | '_allIconLocales'
  | '_allLinkLocales'
  | '_allImageLocales'
  | '_allSubLabelLocales'
  | 'seoMetaTags';

export type BlockSpecificData = {
  appDownloadLinks?: {macos: string; windows: string};
  pageData?: {
    slug: string;
    locale: LocaleEnum;
    pageModelName: string;
    pageOriginalId: string;
    pageModelOriginalId: string;
  };
};

export type BlockBase = {
  __typename: string;
  id: string;
};

/* Basic types */
export type ImageType = Queries.DatoCmsFileField & {
  gatsbyImageData?: IGatsbyImageData;
};

/* Model types */
export type TestimonialType = Queries.DatoCmsTestimonial;

export type SignupFormType = Omit<Queries.DatoCmsSignUpForm, ToExclude | '_allSubtitleLocales' | '_allTitleLocales' | 'locales' | 'subtitleNode'> & {
  locale: LocaleEnum;
  setFormData: React.Dispatch<React.SetStateAction<{email: string; orderId: string; accountAuthToken: string}>>;
};

/* Template Types */
export type BlogPostType = {
  data: Queries.BlogPostPageQueryQuery & {
    datoCmsBlogpost: Queries.BlogPostPageQueryQuery['datoCmsBlogpost'] & {
      model: ModelBaseType['model'] & {
        name: string;
      };
      tocFontSize: TOCFontSizeEnum;
      tocColor: Colors.TRANSPARENT | Colors.PRIMARY | Colors.BRIGHTGREY;
      readingTime: Queries.DatoCmsBlogpost['readingTime'];
      author: Queries.BlogPostPageQueryQuery['datoCmsBlogpost']['author'] & {
        profileImage: ImageType;
      };
    };
    allDatoCmsBlogCategory: {
      nodes: Mutable<Queries.BlogPostPageQueryQuery['allDatoCmsBlogCategory']['nodes']>;
    };
  };
  pageContext: {
    relatedBlogPostLimit: number;
    slugLocales: SlugLocale[];
    locale: LocaleEnum;
    messages: Record<string, string>;
    blockSpecificData: BlockSpecificData;
  };
  location: {
    pathname: string;
  };
};

export type BlogPostPreviewType = NodeWithContentProp &
  Pick<BlogPostType['data']['datoCmsBlogpost'], 'slug' | 'category' | 'title' | 'author'> & {
    readingTime: Queries.DatoCmsBlogpost['readingTime'];
    id: string;
    meta: Pick<BlogPostType['data']['datoCmsBlogpost']['meta'], 'publishedAt'>;
    category: Pick<BlogPostType['data']['datoCmsBlogpost']['category'], 'title'>;
  };

export type BlogOverviewType = {
  pageContext: PageContext & {
    supportedLocales: LocaleEnum[];
    queryData: {
      data: {
        allDatoCmsBlogCategory: {nodes: BlogCategories};
        allDatoCmsBlogpost: {
          group: {
            nodes: (NodeWithContentProp &
              BlogPostType['data']['datoCmsBlogpost'] & {
                category: BlogPostType['data']['datoCmsBlogpost']['category'] & {
                  id: string;
                };
              })[];
          }[];
        };
        allDatoCmsBlogPopularPost: {
          nodes: (ModelBaseType & {
            blogPopularPost: (NodeWithContentProp & BlogPostType['data']['datoCmsBlogpost'])[];
          })[];
        };
      };
    };
  };
  location: {pathname: string};
};

/* Blocks */
export type BlockTypes =
  | BannerWithImageAndButtonType['block']
  | TextWithImageSlimType['block']
  | TextWithVideoType['block']
  | TestimonialSliderSlimType['block']
  | ImageWithDeviceSlimType['block']
  | FAQType['block']
  | NewsLetterType['block']
  | ImageSlimType['block']
  | DatoCmsTwoColumnPriceTableType['block']
  | LinksWithArrowsType['block']
  | ButtonsSlimType['block']
  | HeroLeftAlignedContentSlimType['block']
  | DowloadAppsSlimType['block']
  | HeadlineSlimType['block']
  | VideoType['block']
  | RichTextType['block']
  | DatoCmsBannerType['block']
  | TypeformType['block']
  | PricingCardsSlimType['block']
  | HeadlineWithButtonReferenceType['block']
  | PricingAddOnsSectionType['block']
  | HeadlineWithButtonReferenceType['block']
  | StorylaneDemoType['block']
  | InfoBoxTypeSlim['block']
  | CalendlyWithImageType['block']
  | HeaderType['block']
  | IconCardsWithTextSlimType['block']
  | AccordionSlimType['block']
  | PricingHeaderBlockType['block']
  | BannerWithSignupType['block']
  | TwoColumnHeroSlimType['block']
  | RelatedBlogPostsType['block']
  | DatoCmsHeadlineType['block']
  | BannerWithEmailType['block']
  | HeroWithBackgroundImageSlimType['block']
  | IconCardsSlimBlockType['block']
  | CalendlyType['block']
  | HeroWithBackgroundAndDeviceType['block']
  | ImgCardsSlimType
  | ImgCardType['block']
  | SalesforceLightningOutType['block']
  | ProductVideoTabsSlimType['block']
  | DatoCmsTwoColumnImageRichtextType['block']
  | DatoCmsTwoColumnRichtextImageType['block']
  | HeroWithImageAndDeviceSlimType['block']
  | DatoCmsImageType['block']
  | DatoCmsOneColumnTextType['block']
  | DatoCmsThreeColumnCardType['block']
  | DatoCmsThreeColumnTextType['block']
  | DatoCms2ColumnTextType['block']
  | IconCardsWithTextReferenceType['block']
  | TextWithImageSlimReferenceType['block']
  | FAQReferenceType['block']
  | DatoCms3ColumnYoutubeType['block']
  | DatoCms1ColumnYoutubeType['block']
  | CampaignWithDeviceSlimType['block']
  | SuxxessWidgetType['block']
  | TableOfContentType['block']
  | IconCardsWithIconButtonsType['block']
  | IndustryTeaserType['block']
  | BlogpostCTABannerType['block'];

export type ButtonType = Omit<Queries.DatoCmsButton, ToExclude> & ModelBaseType;
export type IconButtonType = {button: ButtonType & {icon: {name: IconName}}};

export type DatoCmsImageType = {
  block: Queries.blocksPage_DatoCmsImage_Fragment & {
    imageSource: ImageType;
    sectionSpacing: {
      value: SectionSpacing;
    };
  };
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type DatoCms1ColumnYoutubeType = {
  block: Omit<Queries.DatoCms1ColumnYoutube, ToExclude>;
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type DatoCms2ColumnTextType = {
  block: Omit<Queries.DatoCms2ColumnText, ToExclude>;
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type DatoCmsOneColumnTextType = {
  block: Omit<Queries.DatoCmsOneColumnText, ToExclude> & {
    __typename: string;
  };
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type DatoCmsThreeColumnTextType = {
  block: Omit<Queries.DatoCmsThreeColumnText, ToExclude>;
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type DatoCmsThreeColumnCardType = {
  block: Omit<Queries.DatoCmsThreeColumnCard, ToExclude>;
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type IconCardsSlimBlockType = {
  block: Omit<Queries.DatoCmsIconCardsSlim, ToExclude> & {
    columns: 1 | 2 | 3 | 4;
    columnsOnMobile: 1 | 2;
    maxWidth: SectionMaxWidth;
    cardOneIcon: IconName;
    cardTwoIcon: IconName;
    cardThreeIcon: IconName;
    cardFourIcon: IconName;
    headerType: 'h2' | 'h3' | 'h4';
    spacing: SectionSpacing.SMALL | SectionSpacing.NORMAL | SectionSpacing.LARGE | SectionSpacing.TOP_ONLY | SectionSpacing.BOTTOM_ONLY;
    colors: Colors.BRIGHTGREY | Colors.TRANSPARENT;
  };
};

export type ImgCardsSlimType = Omit<
  Queries.DatoCmsImgCardsSlim,
  ToExclude | 'cardOneLink' | 'cardTwoLink' | 'cardThreeLink' | 'cardFourLink' | 'cardOneButton' | 'cardTwoButton' | 'cardThreeButton' | 'cardFourButton'
> & {
  maxWidth: SectionMaxWidth;
  columns: 1 | 2 | 3 | 4;
  cardOneImage: ImageType;
  cardTwoImage: ImageType;
  cardThreeImage: ImageType;
  cardFourImage: ImageType;
  cardOneLink: ButtonType;
  cardTwoLink: ButtonType;
  cardThreeLink: ButtonType;
  cardFourLink: ButtonType;
  cardOneButton: ButtonType[];
  cardTwoButton: ButtonType[];
  cardThreeButton: ButtonType[];
  cardFourButton: ButtonType[];
  spacing: SectionSpacing.SMALL | SectionSpacing.NORMAL | SectionSpacing.LARGE | SectionSpacing.TOP_ONLY | SectionSpacing.BOTTOM_ONLY;
  colors: Colors.BRIGHTGREY | Colors.TRANSPARENT;
};

export type DatoCmsTwoColumnRichtextImageType = {
  block: Omit<Queries.DatoCmsTwoColumnRichtextImage, ToExclude> & {
    __typename: string;
  };
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type DatoCmsTwoColumnImageRichtextType = {
  block: Omit<Queries.DatoCmsTwoColumnImageRichtext, ToExclude> & {
    __typename: string;
  };
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type HeroWithBackgroundImageSlimType = {
  block: Omit<Queries.DatoCmsHeroWithBackgroundImageSlim, ToExclude | 'buttons'> & {
    backgroundImage: ImageType;
    buttons: ButtonType[];
  };
};

export type HeroWithImageAndDeviceSlimType = {
  block: Queries.blocksPage_DatoCmsHeroWithImageAndDeviceSlim_Fragment & {
    deviceImage: ImageType;
    backgroundImage: ImageType;
  };
};

export type TwoColumnHeroSlimType = {
  block: Omit<Queries.DatoCmsTwoColumnHeroSlim, ToExclude | 'buttons'> & {
    image: ImageType;
    imageSmall: ImageType;
    buttons: ButtonType[];
  };
};

export type CampaignWithDeviceSlimType = {
  block: Omit<Queries.DatoCmsCampaignWithDeviceSlim, ToExclude> & {
    backgroundImage: ImageType;
    deviceImage: ImageType;
  };
};

export type PricingCardsSlimType = {
  block: Omit<Queries.DatoCmsPricingCardsSlim, ToExclude> & {
    __typename: string;
    spacing: SectionSpacing.SMALL | SectionSpacing.NORMAL | SectionSpacing.LARGE | SectionSpacing.TOP_ONLY | SectionSpacing.BOTTOM_ONLY;
    colors: Colors.BRIGHTGREY | Colors.TRANSPARENT;
  };
};

export type PricingAddOnsSectionType = {
  block: Omit<Queries.DatoCmsPricingAddOnsSection, ToExclude> & {
    colors: Colors.BRIGHTGREY | Colors.LIGHT;
    spacing: SectionSpacing.SMALL | SectionSpacing.NORMAL | SectionSpacing.LARGE | SectionSpacing.TOP_ONLY;
    maxWidth: Omit<SectionMaxWidth, SectionMaxWidth.MEDIUM>;
    pricingAddOnsCards: Array<PricingAddonCardsType>;
  };
};

export type HeroWithBackgroundAndDeviceType = {
  block: Omit<Queries.DatoCmsHeroWithBackgroundAndDevice, ToExclude | 'buttons'> & {
    backgroundImage: ImageType;
    buttons: ButtonType[];
    deviceImage: ImageType;
    backgroundImageFocusPoint: 'left center' | 'center center' | 'center right';
  };
};

export type DatoCmsHeadlineType = {
  block: Omit<Queries.DatoCmsHeadline, ToExclude> & {
    __typename: string;
  };
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type ImgCardType = {
  block: Omit<Queries.DatoCmsImgCardsSlim, ToExclude>;
};

export type InfoBoxTypeSlim = {
  block: Omit<Queries.DatoCmsInfoboxSlim, ToExclude> & {
    spacing: SectionSpacing;
    colors: Colors;
  };
};

export type DatoCms3ColumnYoutubeType = {
  block: Omit<Queries.DatoCms3ColumnYoutube, ToExclude>;
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type BannerWithImageAndButtonType = {
  block: Omit<Queries.DatoCmsBannerWithImageAndButton, ToExclude> & {
    __typename: string;
    id: string;
    textAlignment: 'left' | 'right';
    spacing: 'normal' | 'large';
    mobileImage: ImageType;
    backgroundImage: ImageType;
    buttons: ButtonType[];
  };
};

export type TextWithImageSlimType = {
  block: Omit<Queries.DatoCmsTextWithImageSlim, ToExclude> & {
    __typename: string;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY;
    image: ImageType;
    imageSmall: ImageType;
    imageAlignment: 'left' | 'right';
    imageOrientation: 'landscape' | 'portrait';
    imagePosition: 'off' | 'translate-down' | 'translate-up';
  };
};

export type TextWithVideoType = {
  block: Omit<Queries.DatoCmsTextWithVideo, ToExclude> & {
    model: {
      name: string;
    };
    __typename: string;
    id: string;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY;
    videoPosition: 'off' | 'translate-down' | 'translate-up';
    videoAlignment: 'left' | 'right';
    poster: ImageType;
  };
};

export type TestimonialSliderSlimType = {
  block: Queries.DatoCmsTestimonialSliderSlim & {
    __typename: string;
    colors: Colors.BRIGHTGREY | Colors.TRANSPARENT;
    spacing:
      | SectionSpacing.SMALL
      | SectionSpacing.NORMAL
      | SectionSpacing.LARGE
      | SectionSpacing.BOTTOM_ONLY
      | SectionSpacing.BOTTOM_ONLY_LARGE
      | SectionSpacing.TOP_ONLY
      | SectionSpacing.TOP_ONLY_LARGE;
    testimonials: TestimonialType[];
  };
};

export type TestimonialVideoSliderSlimType = {
  block: Queries.DatoCmsTestimonialVideoSliderSlim & {
    __typename: string;
    colors: Colors.BRIGHTGREY | Colors.TRANSPARENT;
    spacing:
      | SectionSpacing.SMALL
      | SectionSpacing.NORMAL
      | SectionSpacing.LARGE
      | SectionSpacing.BOTTOM_ONLY
      | SectionSpacing.BOTTOM_ONLY_LARGE
      | SectionSpacing.TOP_ONLY
      | SectionSpacing.TOP_ONLY_LARGE;
    testimonials: TestimonialType[];
  };
};

export type ImageWithDeviceSlimType = {
  block: Queries.DatoCmsImageWithDeviceSlim & {
    __typename: string;
    deviceImage: ImageType;
    spacing:
      | SectionSpacing.SMALL
      | SectionSpacing.NORMAL
      | SectionSpacing.LARGE
      | SectionSpacing.BOTTOM_ONLY
      | SectionSpacing.BOTTOM_ONLY_LARGE
      | SectionSpacing.TOP_ONLY
      | SectionSpacing.TOP_ONLY_LARGE;
    colors: Colors.BRIGHTGREY | Colors.TRANSPARENT;
    image: ImageType;
  };
};

export type NavigationType = Queries.DatoCmsNavigation;

export type NavigationQueryType = {
  errors: Error;
  data: {
    [LocaleAliasEnum.AT]: {
      nodes: NavigationType[];
    };
    [LocaleAliasEnum.CH]: {
      nodes: NavigationType[];
    };
    [LocaleAliasEnum.DE]: {
      nodes: NavigationType[];
    };
    [LocaleAliasEnum.IE]: {
      nodes: NavigationType[];
    };
  };
};

export type SubmenuItemType = Queries.DatoCmsSubmenuItem;

export type PricingAddonCardsType = Queries.DatoCmsPricingAddOn & {
  locales: Array<LocaleEnum>;
  priceUnit: PriceUnit | CurrencySymbolEnum;
};

export type FAQType = {
  block: Queries.DatoCmsFaq & {
    colors: Colors.BRIGHTGREY | Colors.TRANSPARENT;
    spacing: SectionSpacing.SMALL | SectionSpacing.NORMAL | SectionSpacing.LARGE | SectionSpacing.TOP_ONLY | SectionSpacing.BOTTOM_ONLY;
  };
};

export type FAQReferenceType = {
  block: {
    spacing: FAQType['block']['spacing'];
    colors: FAQType['block']['colors'];
    faqsReference: FAQType['block'] &
      ModelBaseType & {
        internalTitle: string;
        locales: LocaleEnum[];
      };
  };
};

export type TextWithImageSlimReferenceType = {
  block: {
    __typename: string;
    image: ImageType;
    title: Queries.DatoCmsTextWithImageSlimReference['title'];
    imageAlignment: 'left' | 'right';
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY;
    textWithImageSlimReference: TextWithImageSlimType['block'] &
      ModelBaseType & {
        locales: LocaleEnum[];
        internalTitle: string;
        model: ModelBaseType['model'] & {
          name: string;
        };
      };
  };
};

export type ImageSlimType = {
  block: Queries.DatoCmsImageSlim & {
    __typename: string;
    image: ImageType;
    spacing: SectionSpacing.SMALL | SectionSpacing.NORMAL | SectionSpacing.LARGE | SectionSpacing.TOP_ONLY | SectionSpacing.BOTTOM_ONLY;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY;
    width: ImageSlimWidth;
  };
};

export type LinksWithArrowsType = {
  block: Omit<Queries.DatoCmsLinksSlim, ToExclude> & {
    __typename: string;
    spacing: SectionSpacing.SMALL | SectionSpacing.NORMAL | SectionSpacing.LARGE | SectionSpacing.TOP_ONLY | SectionSpacing.BOTTOM_ONLY;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY | Colors.PRIMARY;
  };
};

export type HeadlineWithButtonReferenceType = {
  block: {
    headlineWithButtonsReference: ModelBaseType & {
      title: string;
      model: ModelBaseType['model'] & {
        name: string;
      };
      locales: LocaleEnum[];
      headline: [HeadlineSlimType['block']];
      buttons: [ButtonsSlimType['block'] & {locales: LocaleEnum}];
    };
  };
};

export type IconCardsWithTextReferenceType = {
  block: {
    __typename: string;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY;
    iconCardsWithTextModel: IconCardsWithTextSlimType['block'] & {locales: LocaleEnum};
  };
  pageData: BlockSpecificData['pageData'];
};

export type SuxxessWidgetType = {
  block: Queries.blocksPage_DatoCmsSuxxessWidget_Fragment & {
    sectionSpacing: {
      value: SectionSpacing;
    };
  };
};

export type IconCardsWithTextSlimType = {
  block: Omit<Queries.DatoCmsIconCardsWithTextSlim, ToExclude> & {
    __typename: string;
    maxWidth: SectionMaxWidth;
    cardThreeIcon: IconName;
    cardTwoIcon: IconName;
    cardOneIcon: IconName;
  };
};

export type DatoCmsTwoColumnPriceTableType = {
  block: Omit<Queries.DatoCmsTwoColumnPriceTable, ToExclude> & {
    __typename: string;
    id: string;
  };
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type TypeformType = {
  block: Omit<Queries.DatoCmsTypeform, ToExclude>;
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type ButtonsSlimType = {
  block: Omit<Queries.DatoCmsButtonsSlim, ToExclude | 'buttons'> & {
    model: {
      name: string;
    };
    buttons: ButtonType[];
    __typename: string;
    spacing:
      | SectionSpacing.SMALL
      | SectionSpacing.NORMAL
      | SectionSpacing.LARGE
      | SectionSpacing.TOP_ONLY
      | SectionSpacing.TOP_ONLY_LARGE
      | SectionSpacing.BOTTOM_ONLY
      | SectionSpacing.BOTTOM_ONLY_LARGE;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY | Colors.PRIMARY;
  };
};

export type DowloadAppsSlimType = {
  block: Queries.DatoCmsDownloadAppsSlim & {
    __typename: string;
    spacing: SectionSpacing;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY;
    maxWidth: SectionMaxWidth;
  };
  appDownloadLinks: BlockSpecificData['appDownloadLinks'];
};

export type HeadlineSlimType = {
  block: Omit<Queries.DatoCmsHeadlineSlim, ToExclude> & {
    __typename: string;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY | Colors.PRIMARY;
    spacing:
      | SectionSpacing.SMALL
      | SectionSpacing.NORMAL
      | SectionSpacing.LARGE
      | SectionSpacing.TOP_ONLY
      | SectionSpacing.TOP_ONLY_LARGE
      | SectionSpacing.BOTTOM_ONLY
      | SectionSpacing.BOTTOM_ONLY_LARGE;
    maxWidth: 'small' | 'normal' | 'large';
    reduceHeadlineWidth: boolean;
    h1: boolean;
    visibility: Visibility;
  };
};

export type BannerWithEmailType = {
  block: Omit<Queries.DatoCmsBannerWithEmail, ToExclude> & {
    image: ImageType;
    spacing:
      | SectionSpacing.SMALL
      | SectionSpacing.NORMAL
      | SectionSpacing.LARGE
      | SectionSpacing.TOP_ONLY
      | SectionSpacing.TOP_ONLY_LARGE
      | SectionSpacing.BOTTOM_ONLY
      | SectionSpacing.BOTTOM_ONLY_LARGE;
  };
};

export type VideoType = {
  block: Omit<Queries.DatoCmsVideo, ToExclude> & {
    __typename: string;
    model: {
      name: string;
    };
    poster: ImageType;
    spacing: SectionSpacing.SMALL | SectionSpacing.NORMAL | SectionSpacing.LARGE | SectionSpacing.TOP_ONLY | SectionSpacing.BOTTOM_ONLY;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY;
  };
};

export type RichTextType = {
  block: Omit<Queries.DatoCmsRichText, ToExclude | 'richTextNode'> & {
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY | Colors.PRIMARY;
    spacing: SectionSpacing.SMALL | SectionSpacing.NORMAL | SectionSpacing.LARGE | SectionSpacing.TOP_ONLY | SectionSpacing.BOTTOM_ONLY;
    margin: SectionMargin.NONE | SectionMargin.TOP_ONLY | SectionMargin.BOTTOM_ONLY | SectionMargin.BOTH;
    roundedCorners: SectionRounded.NONE | SectionRounded.TOP_ONLY | SectionRounded.BOTTOM_ONLY | SectionRounded.BOTH;
  };
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
  isBlogPost: boolean;
};

export type HeaderType = {
  block: Queries.blocksPage_DatoCmsHeader_Fragment & {
    headerType: 'h2' | 'h3' | 'h4';
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY | Colors.PRIMARY;
    roundedCorners: SectionRounded.NONE | SectionRounded.TOP_ONLY | SectionRounded.BOTTOM_ONLY | SectionRounded.BOTH;
    sectionSpacing: {
      value: SectionSpacing;
    };
  };
  isBlogPost: boolean;
};

export type PricingHeaderBlockType = {
  block: Queries.DatoCmsPricingHeaderBlock & {
    pricingHeaderModel: Queries.DatoCmsPricingHeader & {
      locales: LocaleEnum[];
      colors: Colors.BRIGHTGREY | Colors.WHITE;
    };
  };
};

export type AccordionSlimType = {
  block: Omit<Queries.DatoCmsAccordionSlim, ToExclude | 'bodyNode'> & {
    id: UUID;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY;
    spacing: SectionSpacing.SMALL | SectionSpacing.NORMAL | SectionSpacing.LARGE | SectionSpacing.TOP_ONLY | SectionSpacing.BOTTOM_ONLY;
  };
  noPaddingX?: boolean;
};

export type BannerWithSignupType = {
  block: BlockBase & Queries.DatoCmsBannerWithSignup;
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type DatoCmsBannerType = {
  block: Omit<Queries.DatoCmsBanner, ToExclude> & {
    __typename: string;
  };
  nextBlockSameType: boolean;
  previousBlockSameType: boolean;
};

export type ProductVideoTabsSlimType = {
  block: Omit<Queries.DatoCmsProductVideoTabsSlim, 'videoOne' | 'videoTwo' | 'videoThree' | ToExclude> & {
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY;
    spacing: SectionSpacing.SMALL | SectionSpacing.NORMAL | SectionSpacing.LARGE | SectionSpacing.TOP_ONLY | SectionSpacing.BOTTOM_ONLY;
    // DatoCmsFileField Type has many unnecessary properties, we only need the `url`
    videoOne: Pick<Queries.DatoCmsFileField, 'url'>;
    videoTwo: Pick<Queries.DatoCmsFileField, 'url'>;
    videoThree: Pick<Queries.DatoCmsFileField, 'url'>;
  };
  pageData: BlockSpecificData['pageData'];
};

export type RelatedBlogPostsType = {
  block: Omit<Queries.DatoCmsRelatedBlogPostsBlock, ToExclude>;
  __typename: string;
};

export type CalendlyType = {
  block: Omit<Queries.DatoCmsCalendly, ToExclude>;
  calendlyHeight?: string;
  hideText?: boolean;
  unwrapped?: boolean;
  nextBlockSameType?: boolean;
  previousBlockSameType?: boolean;
  initInlineWidget: (opts: {
    url: string;
    parentElement: HTMLElement;
    utm?: {
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
      utmContent?: string;
      utmTerm?: string;
    };
  }) => void;
};

export type SalesforceLightningOutType = {
  block: Omit<Queries.DatoCmsSalesforceForm, ToExclude>;
  use: (wrapperEl: string, callback: () => void, salesforceSitesUrl: string) => void;
  createComponent: (componentName: string, args: Record<string, unknown>, domElement: HTMLElement, callback: (component: unknown) => void) => void;
};

export type NewsLetterType = {
  block: Omit<Queries.DatoCmsNewsletter, ToExclude>;
};

export type HeroLeftAlignedContentSlimType = {
  block: Omit<Queries.DatoCmsHeroWithLeftAlignedContentSlim, ToExclude | 'buttons'> & {
    backgroundImage: ImageType;
    buttons: ButtonType[];
  };
};

export type CalendlyWithImageType = {
  block: Omit<Queries.DatoCmsCalendlyWithImage, ToExclude | 'calendly'> & {
    desktopImage: ImageType;
    mobileImage: ImageType;
    calendly: Array<CalendlyType['block']>;
  };
};

export type ImageWithLogoType = {
  block: Queries.DatoCmsImageWithLogo & {
    __typename: string;
    image: ImageType;
    spacing: SectionSpacing;
  };
};

export type SocialMediaPresenceType = {
  block: Omit<Queries.DatoCmsSocialMediaPresence, ToExclude> & {
    __typename: string;
    spacing: SectionSpacing;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY;
  };
};

export type CustomFooterQueryType = Queries.ActiveFooterQuery;
// All locales share the same structure
// This type is a shortcut
export type CustomFooterQuerySectionsType = Queries.ActiveFooterQuery[LocaleAliasEnum.AT]['nodes'][0]['activeFooter']['sections'];

export type StorylaneDemoType = {
  block: Omit<Queries.DatoCmsStorylaneDemo, ToExclude> & {
    spacing: SectionSpacing;
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY | Colors.PRIMARY;
    visibility: Visibility;
  };
};

export type TableOfContentType = {
  block: Omit<Queries.DatoCmsTableOfContent, ToExclude> & {
    margin: SectionMargin;
    fontSize: TOCFontSizeEnum;
    model: {
      name;
    };
  };
};

export type IconCardsWithIconButtonsType = {
  block: Queries.blocksPage_DatoCmsIconCardsWithIconButtonsBlock_Fragment & {
    color: Colors;
    sectionSpacing: {
      value: SectionSpacing;
    };
  };
};

export type SignUpAbTestingDataType = Queries.ABSignUpFormPageQueryQuery;

export type SignUpTestimonialSliderType = {
  testimonial: SignUpAbTestingDataType['datoCmsSignUpTestimonial']['testimonial'];
};

export type IndustryTeaserType = {
  block: Queries.datoCmsIndustryTeaserFragment & {
    colors: Colors.TRANSPARENT | Colors.BRIGHTGREY;
    sectionSpacing: {
      value: SectionSpacing;
    };
  };
};

export type BlogpostCTABannerType = {
  block: Queries.datoCmsBlogpostCtaBannerFragment & {
    image: ImageType;
    colors: Colors.TRANSPARENT | Colors.PRIMARY | Colors.BRIGHTGREY;
    sectionSpacing: {
      value: SectionSpacing;
    };
  };
};
